import {Alert, Button, Group, Loader, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {useQueryClient} from '@tanstack/react-query'
import {Link} from '@tanstack/react-router'
import {useCallback, useEffect, useRef, useState} from 'react'

import {useDispatchYards} from 'src/dispatch/use-dispatch'

import {invalidateResourceAssignmentQueries} from '../assignments/invalidate-resource-assignment-queries'
import {jobAssignmentStripDataChanged} from '../assignments/job-assignment-strip-data-changed'
import {useCurrentUser} from '../auth/use-current-user'
import {invalidateEquipmentMoves} from '../project-deployed-equipment/invalidate-equipment-moves'
import {invalidateProjectBillableLineItemsQueries} from '../projects/invalidate-project-billable-line-items'
import {useProject, useProjectBillingTerms} from '../projects/use-projects'
import {ConfirmPickupButton} from '../work/confirm-pickup-button'
import {PartialPickupBadge} from '../work/partial-pickup'
import {PickupReadyCheck} from '../work/pickup-ready-check'
import {PickupSoonerButton} from '../work/pickup-sooner-button'
import {type CustomerDataLayer, customerDataLayer} from './customer-data-layer'
import {canDispatchWork, canEditSales, editBlockedNote} from './edit-rights'
import {EditRightsProvider} from './edit-rights-context'
import {financialLineItemsChanged} from './financial-line-item-form-utils'
import {JobBillingTermsSection} from './job-billing-terms-section'
import {JobCommentsSection} from './job-comments-section'
import {JobCustomerPocSection} from './job-customer-poc-section'
import {JobCustomerSection} from './job-customer-section'
import {type JobDataLayer, jobDataLayer} from './job-data-layer'
import {JobDetailsPageTitle} from './job-details-page-title'
import {JobDetailsSection} from './job-details-section'
import {JobDocumentsSection} from './job-documents-section'
import {JobInvoicingSection} from './job-invoicing-section'
import {JobLocationsSection} from './job-locations-section'
import {JobScheduleSection} from './job-schedule-section'
import {JobTimeEntriesSection} from './job-time-entries-section'
import {JobLoadsSection} from './loads/job-loads-section'
import {jobLoadsKeys} from './loads/query-keys'
import {OrderJobsCard} from './order-plus/order-jobs-card'
import {OrderPlusButtons} from './order-plus/order-plus-buttons'
import {RequirementsAssignmentsSection} from './requirements-assignments-section'
import {RerentedEquipmentSection} from './rerented-equipment-section'
import type {JobDetail} from './types'

// Reset the browser's default fieldset styles
const SALES_FIELDSET_STYLE = {border: 0, padding: 0, margin: 0, minWidth: 0}

export type JobDetailsPageState = 'loading' | 'error' | 'notFound' | 'ready'

type JobDetailsPageProps = {
    jobId: string
    dataLayer?: JobDataLayer
    customerDataLayerProp?: CustomerDataLayer
}

export function JobDetailsPage({
    jobId,
    dataLayer = jobDataLayer,
    customerDataLayerProp = customerDataLayer,
}: JobDetailsPageProps) {
    const queryClient = useQueryClient()
    const currentUser = useCurrentUser()

    const currentUserId = currentUser?.id
    const [pageState, setPageState] = useState<JobDetailsPageState>('loading')
    const [job, setJob] = useState<JobDetail | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const loadRequestId = useRef(0)

    const handleJobUpdated = useCallback(
        (updatedJob: JobDetail) => {
            const assignmentsChanged = job != null && jobAssignmentStripDataChanged(job, updatedJob)
            if (assignmentsChanged) {
                invalidateResourceAssignmentQueries(queryClient)
            }
            // Refresh loads when assignments or status change
            if (
                assignmentsChanged ||
                job?.status !== updatedJob.status ||
                job.didNotHappenAt !== updatedJob.didNotHappenAt
            ) {
                void queryClient.invalidateQueries({queryKey: jobLoadsKeys.forJob(updatedJob.id)})
            }
            if (
                job &&
                financialLineItemsChanged(job.financialLineItems, updatedJob.financialLineItems)
            ) {
                invalidateProjectBillableLineItemsQueries(queryClient, updatedJob.projectId)
            }
            void invalidateEquipmentMoves(queryClient, updatedJob.projectId)
            setJob(updatedJob)
        },
        [job, queryClient],
    )

    const loadJob = useCallback(async () => {
        const requestId = ++loadRequestId.current
        setPageState('loading')
        setErrorMessage(null)

        try {
            const loadedJob = await dataLayer.getJob(jobId)
            if (requestId !== loadRequestId.current) {
                return
            }

            setJob(loadedJob)
            setPageState('ready')
        } catch (error) {
            if (requestId !== loadRequestId.current) {
                return
            }

            const message = error instanceof Error ? error.message : 'Failed to load job'
            if (message.toLowerCase().includes('not found')) {
                setPageState('notFound')
                return
            }

            setErrorMessage(message)
            setPageState('error')
        }
    }, [dataLayer, jobId])

    useEffect(() => {
        void loadJob()
    }, [loadJob])

    if (pageState === 'loading') {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (pageState === 'notFound') {
        return (
            <Stack gap="md">
                <Title order={1}>Job not found</Title>
                <Text c="dimmed">This job may have been removed or the link is invalid.</Text>
                <Button component={Link} to="/dispatch" variant="light">
                    Back to dispatch
                </Button>
            </Stack>
        )
    }

    if (pageState === 'error' || !job) {
        return (
            <Stack gap="md">
                <Title order={1}>Job details</Title>
                <Alert color="red" title="Could not load job">
                    {errorMessage ?? 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void loadJob()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    return (
        <JobDetailsPageContent
            job={job}
            dataLayer={dataLayer}
            customerDataLayerProp={customerDataLayerProp}
            onJobUpdated={handleJobUpdated}
            currentUserId={currentUserId}
        />
    )
}

type JobDetailsPageContentProps = {
    job: JobDetail
    dataLayer: JobDataLayer
    customerDataLayerProp: CustomerDataLayer
    onJobUpdated: (job: JobDetail) => void
    currentUserId?: string
}

function JobDetailsPageContent({
    job,
    dataLayer,
    customerDataLayerProp,
    onJobUpdated,
    currentUserId,
}: JobDetailsPageContentProps) {
    const {data: project} = useProject(job.projectId)
    const {data: billingTerms = []} = useProjectBillingTerms(job.projectId)
    const currentUser = useCurrentUser()
    const {data: yards = []} = useDispatchYards()
    const workYardId = project?.dispatchYardId ?? null
    const canEdit = canEditSales(currentUser, job, workYardId)
    const canDispatch = canDispatchWork(currentUser, job, workYardId)
    const readOnlyNote = editBlockedNote({
        user: currentUser,
        owners: job,
        repName: job.repName,
        workYardId,
        workYardName: yards.find((yard) => yard.id === workYardId)?.name,
        what: 'job',
    })
    const pickupOpen = job.isPickup && (job.status === 'estimate' || job.status === 'in_progress')
    const canAddToOrder = canEdit && job.status !== 'canceled'

    async function reloadJob() {
        onJobUpdated(await dataLayer.getJob(job.id))
    }

    return (
        <EditRightsProvider rights={{canEdit, canDispatch}}>
            <Stack gap="md">
                <JobDetailsPageTitle
                    job={job}
                    projectName={project?.name ?? null}
                    jobDataLayer={dataLayer}
                    onJobUpdated={onJobUpdated}
                    canEdit={canEdit}
                    actions={
                        <>
                            {pickupOpen && canDispatch ? (
                                <ConfirmPickupButton
                                    projectId={job.projectId}
                                    jobId={job.id}
                                    onConfirmed={() => void reloadJob()}
                                />
                            ) : null}
                            {canAddToOrder ? (
                                <>
                                    {!job.isPickup || pickupOpen ? (
                                        <PickupSoonerButton
                                            projectId={job.projectId}
                                            fromJobId={job.isPickup ? null : job.id}
                                            pickupJobId={job.isPickup ? job.id : null}
                                            onDone={() => void reloadJob()}
                                        />
                                    ) : null}
                                    <OrderPlusButtons
                                        projectId={job.projectId}
                                        sourceJobId={job.id}
                                        pickupFromJobId={job.id}
                                    />
                                </>
                            ) : null}
                        </>
                    }
                />

                <PartialPickupBadge partialPickup={job.partialPickup} />

                {project ? <OrderJobsCard order={project} currentJobId={job.id} /> : null}

                {pickupOpen ? (
                    <PickupReadyCheck
                        projectId={job.projectId}
                        jobId={job.id}
                        startDate={job.startDate}
                        dateTbc={job.dateTbc ?? false}
                        confirmedAt={job.pickupConfirmedAt ?? null}
                        confirmedByName={job.pickupConfirmedByName ?? null}
                        canConfirm={canDispatch}
                        onConfirmed={() => void reloadJob()}
                    />
                ) : null}

                {readOnlyNote ? (
                    <Alert color="gray" variant="light" data-testid="job-read-only-note">
                        {readOnlyNote}
                    </Alert>
                ) : null}

                <fieldset disabled={!canEdit} style={SALES_FIELDSET_STYLE}>
                    <Stack gap="md">
                        <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                            <JobCustomerSection
                                job={job}
                                jobDataLayer={dataLayer}
                                customerDataLayer={customerDataLayerProp}
                                onJobUpdated={onJobUpdated}
                            />

                            <JobDetailsSection
                                job={job}
                                jobDataLayer={dataLayer}
                                onJobUpdated={onJobUpdated}
                                billingTerms={billingTerms}
                            />
                        </SimpleGrid>

                        {project?.internalContact ? (
                            <Paper withBorder p="md" radius="md">
                                <Stack gap="xs">
                                    <Title order={4}>Internal contact</Title>
                                    <Text size="sm">{project.internalContact.name}</Text>
                                    <Text size="sm">{project.internalContact.email}</Text>
                                </Stack>
                            </Paper>
                        ) : null}

                        <JobCustomerPocSection
                            job={job}
                            jobDataLayer={dataLayer}
                            onJobUpdated={onJobUpdated}
                        />

                        <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                            <JobBillingTermsSection projectId={job.projectId} />

                            <JobScheduleSection
                                job={job}
                                jobDataLayer={dataLayer}
                                onJobUpdated={onJobUpdated}
                            />
                        </SimpleGrid>
                    </Stack>
                </fieldset>

                <fieldset disabled={!canEdit} style={SALES_FIELDSET_STYLE}>
                    <Stack gap="md">
                        <RequirementsAssignmentsSection
                            job={job}
                            jobDataLayer={dataLayer}
                            onJobUpdated={onJobUpdated}
                        />

                        <RerentedEquipmentSection job={job} onJobUpdated={onJobUpdated} />

                        <JobTimeEntriesSection
                            job={job}
                            jobDataLayer={dataLayer}
                            onJobUpdated={onJobUpdated}
                        />

                        <JobLoadsSection
                            job={job}
                            onChargesChanged={() =>
                                void dataLayer.getJob(job.id).then(onJobUpdated, () => {})
                            }
                        />
                    </Stack>
                </fieldset>

                <fieldset disabled={!canEdit} style={SALES_FIELDSET_STYLE}>
                    <Stack gap="md">
                        <JobInvoicingSection
                            job={job}
                            jobDataLayer={dataLayer}
                            onJobUpdated={onJobUpdated}
                        />

                        <JobLocationsSection
                            job={job}
                            jobDataLayer={dataLayer}
                            onJobUpdated={onJobUpdated}
                        />

                        <JobCommentsSection
                            job={job}
                            onJobUpdated={onJobUpdated}
                            jobDataLayer={dataLayer}
                            currentUserId={currentUserId}
                        />

                        <JobDocumentsSection jobId={job.id} customerId={job.customerId} />
                    </Stack>
                </fieldset>
            </Stack>
        </EditRightsProvider>
    )
}
